import { z } from 'zod';
import { NeedEffects } from './play';

// Rail Co. (as Lagos Life's Bus Co.): a player starts a train company (a name, a paint job, a slogan on the back),
// buys trains (a commuter unit, an express or a night sleeper) for routes out of King's Cross to the map's stops,
// hires their crews and sets the fares. Every evening at 6 pm the walk-ins at King's Cross fill the seats (fewer
// the dearer the fare, more on busy days, fewer when a train is run down) and the takings come in, less the
// station's levy and the train's energy; now and then one breaks down. Every Saturday at 9 am the week's bills go
// out: crews, track access, engineers, insurance; crews that are not paid walk off and their trains stand. Trains
// wear (engineers slow it), are serviced, lose value week by week and can be sold back. Other players see the
// trains in the ticket office and ride them: their fare goes to the company. Everything here is content the
// admins edit.

const id = z.string().regex(/^[a-z0-9_]{2,30}$/);
const icon = z.string().trim().min(1).max(16);
const colour = z.string().regex(/^#[0-9a-f]{6}$/i);
const pence = (max: number) => z.number().int().min(0).max(max);
const effects = NeedEffects;

// the people a train needs (and one it can do without, losing some riders)
export const CREW_ROLES = ['driver', 'driver2', 'guard', 'host'] as const;
export type CrewRole = (typeof CREW_ROLES)[number];
export const CREW_ROLE_LABELS: Record<CrewRole, string> = {
  driver: 'Driver',
  driver2: 'Relief driver',
  guard: 'Guard',
  host: 'Sleeper host',
};
const Crew = z.object({ role: z.enum(CREW_ROLES), wagePence: pence(10_000_000) });

export const TRAIN_KINDS = ['commuter', 'express', 'sleeper'] as const;
export type TrainKind = (typeof TRAIN_KINDS)[number];
export const TrainType = z.object({
  label: z.string().trim().min(2).max(30),
  icon,
  seats: z.number().int().min(1).max(2000),
  pricePence: pence(10_000_000_000),
  // its fare compared with the route's (a sleeper's berth costs double)
  times: z.number().min(0.5).max(5),
  // riders' liking for it, on top of the fare (0 to 0.5)
  appeal: z.number().min(0).max(0.5),
  // what one evening's run costs in energy
  energyPence: pence(100_000_000),
  required: z.array(Crew).min(1).max(3),
  optional: Crew.extend({ penalty: z.number().min(0).max(0.5) }).optional(),
  // a week's track access and insurance
  accessPence: pence(100_000_000),
  insurePence: pence(100_000_000),
  // what the ride does to a rider (a berth: you arrive rested)
  arrive: effects,
  perk: z.string().trim().min(1).max(80),
});
export type TrainType = z.infer<typeof TrainType>;

export const Livery = z.object({ name: z.string().trim().min(2).max(20), body: colour, stripe: colour });
export type Livery = z.infer<typeof Livery>;

export const RailCoRules = z.object({
  registerPence: pence(1_000_000_000),
  renamePence: pence(1_000_000_000),
  // per train
  repaintPence: pence(1_000_000_000),
  maxTrains: z.number().int().min(1).max(50),
  engineerWagePence: pence(100_000_000),
  // trains one engineer looks after, and how many a company may hire
  engineerCovers: z.number().int().min(1).max(20),
  maxEngineers: z.number().int().min(0).max(10),
  // the station's cut of the takings
  levy: z.number().min(0).max(0.5),
  // condition lost a day (with an engineer), and below which a train cannot run
  wear: z.number().min(0).max(20),
  wearEngineer: z.number().min(0).max(20),
  noStart: z.number().int().min(0).max(60),
  // a breakdown's repair (of the price), and the excess when insured
  repairShare: z.number().min(0).max(0.2),
  excessShare: z.number().min(0).max(0.2),
  // a service: of the price, per point of condition put back
  serviceShare: z.number().min(0).max(0.01),
  // walk-ins at King's Cross a company's trains share each evening, and the most it takes in an evening
  allocation: z.number().int().min(1).max(100_000),
  takingsCapPence: pence(10_000_000_000),
  // sold back: this share of the price, less this much a week owned, never below the floor
  resaleShare: z.number().min(0).max(1),
  resaleWeekly: z.number().min(0).max(0.1),
  resaleFloor: z.number().min(0).max(1),
  // the fare's range (of the going rate), busy weekdays (Monday first), and how often it may change
  fareLow: z.number().min(0.1).max(1),
  fareHigh: z.number().min(1).max(5),
  weekdays: z.array(z.number().min(0).max(3)).length(7),
  fareCooldownMinutes: z.number().int().min(0).max(1440),
  // the most catching up done for evenings nobody looked at
  catchUp: z.number().int().min(1).max(14),
});
export type RailCoRules = z.infer<typeof RailCoRules>;

export const RailCoContent = z.object({
  visible: z.boolean(),
  rules: RailCoRules,
  trains: z.object({ commuter: TrainType, express: TrainType, sleeper: TrainType }),
  liveries: z.array(Livery).min(1).max(16),
  // crews by name, and the engineer
  crewNames: z.array(z.string().trim().min(2).max(24)).min(1).max(30),
  engineerName: z.string().trim().min(2).max(30),
  // names players can't take (the real operators)
  lockedNames: z.array(z.string().trim().min(2).max(40)).max(60),
});
export type RailCoContent = z.infer<typeof RailCoContent>;

export const DEFAULT_RAILCO: RailCoContent = {
  visible: true,
  rules: {
    registerPence: 200_000,
    renamePence: 50_000,
    repaintPence: 25_000,
    maxTrains: 12,
    engineerWagePence: 40_000,
    engineerCovers: 4,
    maxEngineers: 3,
    levy: 0.1,
    wear: 2.5,
    wearEngineer: 1.25,
    noStart: 15,
    repairShare: 0.03,
    excessShare: 0.003,
    serviceShare: 0.0004,
    allocation: 400,
    takingsCapPence: 5_000_000,
    resaleShare: 0.6,
    resaleWeekly: 0.005,
    resaleFloor: 0.3,
    fareLow: 0.6,
    fareHigh: 2,
    weekdays: [1.35, 1.25, 1.25, 1.35, 1.6, 1.45, 1.55],
    fareCooldownMinutes: 10,
    catchUp: 3,
  },
  trains: {
    commuter: {
      label: 'Commuter unit',
      icon: '🚈',
      seats: 160,
      pricePence: 25_000_000,
      times: 1,
      appeal: 0,
      energyPence: 12_000,
      required: [{ role: 'driver', wagePence: 40_000 }],
      accessPence: 15_000,
      insurePence: 20_000,
      arrive: { energy: -10, hygiene: -6 },
      perk: 'Cheapest to run. Fills fast on busy days',
    },
    express: {
      label: 'Express',
      icon: '🚄',
      seats: 300,
      pricePence: 90_000_000,
      times: 1,
      appeal: 0.1,
      energyPence: 30_000,
      required: [{ role: 'driver', wagePence: 45_000 }],
      optional: { role: 'guard', wagePence: 20_000, penalty: 0.08 },
      accessPence: 40_000,
      insurePence: 90_000,
      arrive: { energy: -4, fun: 4 },
      perk: 'Air-con, tables and a trolley: riders pick it first',
    },
    sleeper: {
      label: 'Night sleeper',
      icon: '🛏️',
      seats: 80,
      pricePence: 160_000_000,
      times: 2,
      appeal: 0.15,
      energyPence: 40_000,
      required: [
        { role: 'driver', wagePence: 45_000 },
        { role: 'driver2', wagePence: 45_000 },
      ],
      optional: { role: 'host', wagePence: 25_000, penalty: 0.05 },
      accessPence: 50_000,
      insurePence: 160_000,
      arrive: { energy: 10, hygiene: -4 },
      perk: 'Berths: riders arrive rested, and pay double',
    },
  },
  liveries: [
    { name: 'Royal Purple', body: '#5b2a86', stripe: '#f3c614' },
    { name: 'Thames Blue', body: '#1f4fc4', stripe: '#ffffff' },
    { name: 'Racing Green', body: '#0f5a3d', stripe: '#f2d16b' },
    { name: 'Post Box Red', body: '#c8102e', stripe: '#ffffff' },
    { name: 'Pearl White', body: '#f7f7f2', stripe: '#1d2433' },
    { name: 'Midnight', body: '#1b1f3b', stripe: '#f4a300' },
    { name: 'Sky', body: '#46a8e6', stripe: '#ffffff' },
    { name: 'Saffron', body: '#f2a81d', stripe: '#1b1f3b' },
    { name: 'Slate', body: '#56616c', stripe: '#e8c23a' },
    { name: 'Silver Line', body: '#c7ccd1', stripe: '#0f5a3d' },
  ],
  crewNames: ['Big Dave', 'Auntie Pat', 'Kwame', 'Siobhan', 'Raj', 'Old Terry', 'Marcus', 'Bev', 'Dimitri', 'Joyce'],
  engineerName: 'Gary the Engineer',
  lockedNames: [
    'LNER',
    'London North Eastern Railway',
    'Thameslink',
    'Great Northern',
    'Elizabeth line',
    'Transport for London',
    'TfL',
    'Southern',
    'Southeastern',
    'Avanti West Coast',
    'Lumo',
    'Grand Central',
    'Hull Trains',
    'Eurostar',
    'Network Rail',
    'British Rail',
    'Caledonian Sleeper',
    'Great Western Railway',
    'GWR',
    'CrossCountry',
    'Chiltern Railways',
    'c2c',
    'Greater Anglia',
    'London Overground',
    'Heathrow Express',
    'Gatwick Express',
    'DLR',
    'Docklands Light Railway',
  ],
};

export const railCoOf = (content: { railco?: RailCoContent }) => content.railco ?? DEFAULT_RAILCO;

export function railCoProblems(content: RailCoContent): string[] {
  const problems: string[] = [];
  const { rules } = content;
  if (rules.fareLow >= rules.fareHigh) problems.push('Rail Co.: the lowest fare must be below the highest.');
  if (rules.resaleFloor > rules.resaleShare) problems.push('Rail Co.: the resale floor is above the resale share.');
  for (const kind of TRAIN_KINDS) {
    const train = content.trains[kind];
    const roles = train.required.map((crew) => crew.role);
    if (new Set(roles).size !== roles.length) problems.push(`Rail Co.: ${train.label} needs the same role twice.`);
    if (train.optional && roles.includes(train.optional.role))
      problems.push(`Rail Co.: ${train.label}'s optional crew is also required.`);
  }
  return problems;
}

// ---------- the pure parts (the server works the books; the app shows the same numbers) ----------

// a route: King's Cross to a stop, at the stop's Standard fare
export type RailRoute = { placeId: string; name: string; farePence: number };
const roundTo = (value: number, step = 10) => step * Math.round(value / step);
// what a seat may cost on a route, and its going rate
export function fareBand(content: RailCoContent, route: RailRoute, kind: TrainKind) {
  const ref = route.farePence * content.trains[kind].times;
  return {
    low: Math.max(10, roundTo(ref * content.rules.fareLow)),
    high: roundTo(ref * content.rules.fareHigh),
    ref: roundTo(ref),
  };
}
export const clampFare = (content: RailCoContent, route: RailRoute, kind: TrainKind, fare: number) => {
  const band = fareBand(content, route, kind);
  return Math.min(band.high, Math.max(band.low, roundTo(fare)));
};
// the fare set to follow the week: busier days dearer, quiet ones cheaper (Monday 0)
export function autoFare(content: RailCoContent, route: RailRoute, kind: TrainKind, weekday: number) {
  const busy = content.rules.weekdays[weekday] ?? 1;
  const average = content.rules.weekdays.reduce((sum, item) => sum + item, 0) / 7;
  const lean = busy > average + 0.1 ? 1.1 : busy < average - 0.05 ? 0.9 : 1;
  return clampFare(content, route, kind, fareBand(content, route, kind).ref * lean);
}

// how full a train runs (0 to 0.95): busier days, its appeal, how well kept it is, its optional crew, and the fare
export function loadFactor(
  content: RailCoContent,
  values: {
    kind: TrainKind;
    route: RailRoute;
    farePence: number;
    weekday: number;
    condition: number;
    withOptional: boolean;
    luck?: number;
  },
) {
  const train = content.trains[values.kind];
  const ref = fareBand(content, values.route, values.kind).ref || 1;
  const keep = values.condition >= 70 ? 0.05 : values.condition >= 40 ? 0 : -0.15;
  const missing = train.optional && !values.withOptional ? train.optional.penalty : 0;
  const busy = content.rules.weekdays[values.weekday] ?? 1;
  const load = busy + train.appeal + keep - missing - 0.75 * (values.farePence / ref);
  return Math.min(0.95, Math.max(0, load)) * (values.luck ?? 1);
}
// breakdowns are likelier the worse a train is kept
export const breakdownOdds = (content: RailCoContent, condition: number) =>
  condition >= 70 ? 0.02 : condition >= 40 ? 0.06 : condition >= content.rules.noStart ? 0.15 : 1;
// what a service costs (putting the condition back to 100)
export const serviceCostPence = (content: RailCoContent, kind: TrainKind, condition: number) =>
  100 * Math.ceil((Math.max(0, 100 - condition) * content.trains[kind].pricePence * content.rules.serviceShare) / 100);
// what a train sells back for, after this many weeks owned
export const resalePence = (content: RailCoContent, kind: TrainKind, weeksOwned: number) =>
  Math.round(
    content.trains[kind].pricePence *
      Math.max(
        content.rules.resaleFloor,
        content.rules.resaleShare - content.rules.resaleWeekly * Math.max(0, weeksOwned),
      ),
  );
// a week's bill for one train: its crew, its track access and (insured) its insurance
export function trainWeekPence(content: RailCoContent, kind: TrainKind, crew: CrewRole[], insured: boolean) {
  const train = content.trains[kind];
  const wages = crew.reduce(
    (sum, role) =>
      sum +
      (train.required.find((item) => item.role === role)?.wagePence ??
        (train.optional?.role === role ? train.optional.wagePence : 0)),
    0,
  );
  return { wages, access: train.accessPence, insurance: insured ? train.insurePence : 0 };
}
// a train runs with its required crew, in good enough condition, not parked
export const crewComplete = (content: RailCoContent, kind: TrainKind, crew: CrewRole[]) =>
  content.trains[kind].required.every((item) => crew.includes(item.role));
// safety, as riders see it: 0 run down, 1 fine, 2 well kept and insured
export const safetyOf = (condition: number, insured: boolean) =>
  condition < 40 ? 0 : insured && condition >= 70 ? 2 : 1;

// "What would one train make?": an ordinary week of one new train at the going rate (or a fare given), the
// station's walk-ins only
export function weekPlan(
  content: RailCoContent,
  values: { kind: TrainKind; route: RailRoute; farePence?: number; withOptional?: boolean; insured?: boolean },
) {
  const train = content.trains[values.kind];
  const band = fareBand(content, values.route, values.kind);
  const fare =
    values.farePence === undefined ? band.ref : clampFare(content, values.route, values.kind, values.farePence);
  const withOptional = values.withOptional !== false && Boolean(train.optional);
  const insured = values.insured !== false;
  let riders = 0;
  let takings = 0;
  for (let weekday = 0; weekday < 7; weekday += 1) {
    const load = loadFactor(content, {
      kind: values.kind,
      route: values.route,
      farePence: fare,
      weekday,
      condition: 85,
      withOptional,
    });
    const seats = Math.min(train.seats, content.rules.allocation, Math.round(train.seats * load));
    riders += seats;
    takings += Math.min(content.rules.takingsCapPence, seats * fare);
  }
  const levy = Math.round(takings * content.rules.levy);
  const energy = train.energyPence * 7;
  const repairs = Math.round(
    7 *
      breakdownOdds(content, 85) *
      train.pricePence *
      (insured ? content.rules.excessShare : content.rules.repairShare),
  );
  const crew = [
    ...train.required.map((item) => item.role),
    ...(withOptional && train.optional ? [train.optional.role] : []),
  ];
  const bill = trainWeekPence(content, values.kind, crew, insured);
  const cash = takings - levy - energy - repairs - bill.wages - bill.access - bill.insurance;
  const lostValue = Math.round(train.pricePence * content.rules.resaleWeekly);
  return {
    fare,
    band,
    ridersPerEvening: Math.round(riders / 7),
    takings,
    cash,
    paybackWeeks: cash > 0 ? Math.ceil(train.pricePence / cash) : null,
    lostValue,
  };
}

// a company's name: not empty, not too long, not a real operator's
export function nameProblem(content: RailCoContent, name: string) {
  const clean = name.trim().replace(/\s+/g, ' ');
  if (clean.length < 2) return 'Give your company a name';
  if (clean.length > 24) return 'Up to 24 letters';
  const squash = (text: string) => text.toLowerCase().replace(/[^a-z0-9]/g, '');
  const mine = squash(clean);
  const words = new Set(
    clean
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean),
  );
  for (const locked of content.lockedNames) {
    const theirs = squash(locked);
    if (!theirs) continue;
    if (mine === theirs || mine.includes(theirs) || (theirs.length <= 4 && words.has(locked.toLowerCase())))
      return `${locked} is a real operator's name`;
  }
  return null;
}
// a train's tag: the company name's first word, three letters, and its number ("BOR 01")
export function trainTag(companyName: string, index: number) {
  const word =
    companyName
      .toUpperCase()
      .replace(/[^A-Z ]/g, '')
      .split(' ')
      .find((item) => item.length >= 2) ?? 'RCO';
  return `${word.slice(0, 3)} ${String(index + 1).padStart(2, '0')}`;
}

// ---------- over the wire ----------
export const TrainView = z.object({
  id: z.string().uuid(),
  tag: z.string(),
  kind: z.enum(TRAIN_KINDS),
  route: z.string(),
  condition: z.number(),
  // why it stands, when it does: the owner parked it, no crew, worn out
  parked: z.enum(['own', 'crew', 'worn']).nullable(),
  crew: z.array(z.enum(CREW_ROLES)),
  pricePence: z.number().int(),
  resalePence: z.number().int(),
  servicePence: z.number().int(),
  boughtAt: z.string(),
  // the last evening's run
  last: z
    .object({
      day: z.string(),
      riders: z.number().int(),
      farePence: z.number().int(),
      netPence: z.number().int(),
      broke: z.boolean(),
    })
    .nullable(),
});
export type TrainView = z.infer<typeof TrainView>;
export const RailCompanyView = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slogan: z.string().nullable(),
  body: z.string(),
  stripe: z.string(),
  engineers: z.number().int(),
  insured: z.boolean(),
  // fares set per route and kind (pence), and those that follow the week
  fares: z.record(z.string(), z.number().int()),
  auto: z.array(z.string()),
  stars: z.number().nullable(),
  ratings: z.number().int(),
  trains: z.array(TrainView),
  // the week's bill, due Saturday 9 am
  bill: z.object({
    wages: z.number().int(),
    access: z.number().int(),
    engineers: z.number().int(),
    insurance: z.number().int(),
    total: z.number().int(),
    due: z.string(),
  }),
  fleetValuePence: z.number().int(),
  // what happened lately: takings, bills, breakdowns, walk-offs
  events: z.array(
    z.object({
      at: z.string(),
      kind: z.string(),
      amountPence: z.number().int(),
      detail: z.record(z.string(), z.unknown()),
    }),
  ),
  fareLockedUntil: z.string().nullable(),
});
export type RailCompanyView = z.infer<typeof RailCompanyView>;
export const RailCompanyReply = z.object({
  company: RailCompanyView.nullable(),
  walletPence: z.number().int(),
  message: z.string().optional(),
});
export type RailCompanyReply = z.infer<typeof RailCompanyReply>;

const name = z.string().trim().min(2).max(24);
const slogan = z.string().trim().max(40);
export const StartRailCompanyRequest = z.object({ name, slogan: slogan.optional(), body: colour, stripe: colour });
export type StartRailCompanyRequest = z.infer<typeof StartRailCompanyRequest>;
export const BuyTrainRequest = z.object({ kind: z.enum(TRAIN_KINDS), route: id, withOptional: z.boolean() });
export type BuyTrainRequest = z.infer<typeof BuyTrainRequest>;
// what an owner does with a train
export const TrainActionRequest = z.discriminatedUnion('action', [
  z.object({ action: z.literal('service') }),
  z.object({ action: z.literal('park'), parked: z.boolean() }),
  z.object({ action: z.literal('move'), route: id }),
  z.object({ action: z.literal('optional'), on: z.boolean() }),
  z.object({ action: z.literal('sell') }),
]);
export type TrainActionRequest = z.infer<typeof TrainActionRequest>;
// what an owner does with the company
export const RailCompanyActionRequest = z.discriminatedUnion('action', [
  z.object({ action: z.literal('fare'), route: id, kind: z.enum(TRAIN_KINDS), farePence: z.number().int().min(1) }),
  z.object({ action: z.literal('auto'), route: id, kind: z.enum(TRAIN_KINDS), on: z.boolean() }),
  z.object({ action: z.literal('engineers'), count: z.number().int().min(0).max(10) }),
  z.object({ action: z.literal('insurance'), on: z.boolean() }),
  z.object({ action: z.literal('rename'), name, slogan: slogan.optional() }),
  z.object({ action: z.literal('repaint'), body: colour, stripe: colour }),
]);
export type RailCompanyActionRequest = z.infer<typeof RailCompanyActionRequest>;
// a rider's stars for the company that carried them
export const RateTripRequest = z.object({ tripId: z.string().uuid(), stars: z.number().int().min(1).max(5) });
export type RateTripRequest = z.infer<typeof RateTripRequest>;

// players' trains in the ticket office: one row per company, kind and route
export const PlayerTrainOffer = z.object({
  companyId: z.string().uuid(),
  name: z.string(),
  slogan: z.string().nullable(),
  body: z.string(),
  stripe: z.string(),
  kind: z.enum(TRAIN_KINDS),
  route: z.string(),
  farePence: z.number().int(),
  seatsLeft: z.number().int(),
  safety: z.number().int(),
  stars: z.number().nullable(),
  ratings: z.number().int(),
});
export type PlayerTrainOffer = z.infer<typeof PlayerTrainOffer>;
export const PlayerTrainsReply = z.object({ offers: z.array(PlayerTrainOffer) });
export type PlayerTrainsReply = z.infer<typeof PlayerTrainsReply>;
